import { Timestamp } from "firebase/firestore";
import { defaultPaymentSchedule } from "../_constants/job";

import Decimal from "decimal.js";
import {
  JobContract,
  JobPayment,
  JobV2,
  PaymentScheduleItem,
} from "../@types/job";
import { nearZero } from "./math";
import { join } from "path";

export const generateDefaultPaymentSchedule = (
  grandTotal: number,
  numberOfPayments: number,
  monthsBetweenPayments: number
) => {
  // this will get overwritten by useEffect hook in job form for actually accurate amounts
  const amount = Decimal.div(grandTotal, numberOfPayments).toNumber();
  const dateNow = Timestamp.now();

  return Array.from({ length: numberOfPayments }, (_, i) => {
    if (i === 0) {
      return {
        ...defaultPaymentSchedule[0],
        paymentNumber: i,
        amount,
        dueDate: Timestamp.fromMillis(dateNow.toMillis()),
      };
    } else {
      return {
        ...defaultPaymentSchedule[0],
        paymentNumber: i,
        amount,
        dueDate: null,
      };
    }
  });
};

// Helper function to check if contract collection is needed
export const needsContractCollection = (job: JobV2) => {
  const requirements: string[] = [];

  if (job?.wrd?.required && !job.wrd.isSigned) {
    requirements.push("Wildlife Record");
  }

  const contract = job?.contract;
  if (!contract) {
    return requirements;
  }

  // Check signature requirement
  if (contract.signatureRequired && !contract.isSigned) {
    requirements.push("Signature");
  }

  // Check initial payment requirement
  if (
    contract.hasPaymentPlan &&
    contract.paymentSchedule?.[0]?.method !== "auto" &&
    contract.paymentSchedule?.[0]?.status !== "completed"
  ) {
    requirements.push("Initial Payment");
  }

  // Check payment method requirement
  if (contract.hasPaymentPlan && !contract.stripePaymentMethodId) {
    requirements.push("Payment Method on File");
  }

  return requirements;
};

// Helper function to get consecutive payment failures for current payment number
export const getConsecutivePaymentFailures = (job: JobV2): number => {
  const contract = job?.contract;
  if (
    !contract?.hasPaymentPlan ||
    !contract.failedPayments ||
    !contract.stripePaymentMethodId
  ) {
    return 0;
  }

  // Find the current payment number (next unprocessed payment)
  const currentPayment = contract.paymentSchedule?.find(
    (payment) => payment.status !== "completed"
  );

  if (!currentPayment) {
    return 0; // No current payment to fail
  }

  const currentPaymentNumber = currentPayment.paymentNumber;

  // Filter failed payments for this payment number and sort by date descending
  const failuresForCurrentPayment = contract.failedPayments
    .filter(
      (failure) =>
        failure.paymentNumber === currentPaymentNumber &&
        failure.paymentMethodId === contract.stripePaymentMethodId
    )
    .sort((a, b) => b.date.toMillis() - a.date.toMillis());

  // Count consecutive failures from most recent
  let consecutiveCount = 0;
  for (const failure of failuresForCurrentPayment) {
    // If this payment number has ever succeeded, consecutive count resets
    // (but we only care about failures, so just count all failures for this payment number)
    consecutiveCount++;
  }

  return Math.min(consecutiveCount, 3); // Cap at 3 as per business logic
};

export const outOfBandPaymentReconciliation = (
  job: JobV2,
  amount: number
): {
  contract: JobContract | null;
  schedule: JobContract["paymentSchedule"];
  isFinished: boolean;
} => {
  // Bail early if the job has no contract or the payment is invalid.
  const contract = job.contract;
  if (
    !contract?.hasPaymentPlan ||
    !contract.paymentSchedule?.length ||
    !amount
  ) {
    return {
      contract: contract || null,
      schedule: contract?.paymentSchedule || [],
      isFinished: false,
    };
  }

  // Convert the payment total to Decimal for safe math.
  const totalPaid = new Decimal(amount);
  if (totalPaid.lte(0)) {
    return {
      contract,
      schedule: contract.paymentSchedule,
      isFinished: false,
    };
  }

  if (
    nearZero(new Decimal(job.remainingBalance).toDecimalPlaces(2).toNumber())
  ) {
    return {
      contract: {
        ...contract,
        hasPaymentPlan: false,
        finishedByOutOfBandPayment: true,
      },
      schedule: contract.paymentSchedule,
      isFinished: true,
    };
  }

  // Get only the schedule entries that still need to be collected.
  const paymentsNotCompleted = contract.paymentSchedule.filter(
    (schedulePayment) =>
      schedulePayment.status === "unprocessed" ||
      schedulePayment.status === "failed"
  );

  if (!paymentsNotCompleted.length) {
    return {
      contract: {
        ...contract,
        hasPaymentPlan: false,
        finishedByOutOfBandPayment: true,
      },
      schedule: contract.paymentSchedule,
      isFinished: true,
    };
  }

  // Example scenario used throughout this block:
  // - Three remaining payments of $100 each (total outstanding $300)
  // - Customer makes an out-of-band payment of $100
  // In the end, each installment should drop by roughly $33.33, and the final block
  // will clean up the spare cents.

  // Distribute the payment across the remaining entries.
  // Tracks how much of the out-of-band payment is left to spread
  // Example: starts at $100 because that's what the customer paid
  let remainingToAllocate = totalPaid;
  // Tracks how many installments we still need to adjust
  // Example: starts at 3 because there are three payments left
  let remainingPayments = paymentsNotCompleted.length;
  // Remembers which payment we touched last for rounding adjustments
  // Example: becomes 3 after we finish updating payment #3
  let lastUpdatedPaymentNumber: number | null = null;

  const reconciledPayments = paymentsNotCompleted.map((schedulePayment) => {
    if (remainingToAllocate.lte(0) || remainingPayments <= 0) {
      return schedulePayment;
    }

    // Amount still owed for this installment
    // Example: starts at $100 for each payment
    const paymentAmount = new Decimal(schedulePayment.amount || 0);
    // Portion of the out-of-band payment we want to assign to this installment
    // Example: $100 remaining / 3 payments ≈ $33.33
    const equalShare = remainingToAllocate.div(remainingPayments);
    // Deduct whichever is smaller: the amount owed or this payment's share
    // Example: min($100 owed, ~$33.33 share) ≈ $33.33, so we trim the installment to ~$66.67
    const deduction = Decimal.min(paymentAmount, equalShare);

    // Subtract the deduction and ensure the amount never goes below zero
    // Example: $100 - ~$33.33 ≈ $66.67 for the first payment; subsequent ones behave similarly
    const updatedAmount = Decimal.max(
      paymentAmount.minus(deduction),
      new Decimal(0)
    ).toDecimalPlaces(2);

    remainingToAllocate = remainingToAllocate.minus(deduction);
    remainingPayments -= 1;
    lastUpdatedPaymentNumber = schedulePayment.paymentNumber;

    return {
      ...schedulePayment,
      amount: updatedAmount.toNumber(),
    };
  });

  // Ensure rounding errors don’t introduce a mismatch in the totals by dumping any
  // remaining fractional cents into the last payment that was touched.
  // Example continued: equalShare = 100 / 3 ≈ 33.333…, so after two iterations
  // we’ve used about $66.66. We still have ~$33.34 left, so this block adjusts
  // the last payment to absorb the extra cents and keeps the total aligned.
  if (!remainingToAllocate.equals(0) && lastUpdatedPaymentNumber !== null) {
    const adjustment = remainingToAllocate.toDecimalPlaces(2);
    const indexToAdjust = reconciledPayments.findIndex(
      (item) => item.paymentNumber === lastUpdatedPaymentNumber
    );
    if (indexToAdjust >= 0) {
      const paymentAmount = new Decimal(
        reconciledPayments[indexToAdjust].amount || 0
      ).minus(adjustment);
      reconciledPayments[indexToAdjust] = {
        ...reconciledPayments[indexToAdjust],
        amount: Decimal.max(paymentAmount, 0).toDecimalPlaces(2).toNumber(),
      };
    }
    remainingToAllocate = new Decimal(0);
  }

  // Build a lookup map so we can merge the reconciled entries back into the schedule.
  const reconciledMap = new Map(
    reconciledPayments.map((item) => [item.paymentNumber, item])
  );

  // Replace the affected schedule rows with their reconciled versions.
  const updatedSchedule = contract.paymentSchedule.map((schedulePayment) => {
    return reconciledMap.get(schedulePayment.paymentNumber) || schedulePayment;
  });

  return {
    contract: {
      ...contract,
      paymentSchedule: updatedSchedule,
    },
    schedule: updatedSchedule,
    isFinished: false,
  };
};
